import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Package, Clock, Calendar as CalendarIcon, Search, RefreshCw } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import api from '../../api/clients';
import { formatDate, formatCurrency } from '../../lib/utils';

interface ProductoHistorial {
  id: number;
  nombre: string;
  createdAt: string;
  categoria: string | null;
  precio: number;
  stock: number;
}

export function HistorialProductos() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('todos');

  // Extraemos 'refetch' e 'isFetching' para actualizar manualmente los datos
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['historial', 'productos'],
    queryFn: async () => {
      const res = await api.get('/historial/productos');
      return res.data as { success: boolean; data: ProductoHistorial[]; total: number };
    },
    staleTime: 0, // Garantiza que siempre considere los datos frescos al montar
  });

  const filteredProducts = data?.data?.filter((producto) => {
    const matchesSearch = producto.nombre
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    
    if (filter === 'todos') return matchesSearch;
    
    if (filter === 'hoy') {
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const fechaProducto = new Date(producto.createdAt);
      fechaProducto.setHours(0, 0, 0, 0);
      return matchesSearch && hoy.getTime() === fechaProducto.getTime();
    }
    
    if (filter === 'semana') {
      const semanaAtras = new Date();
      semanaAtras.setDate(semanaAtras.getDate() - 7);
      semanaAtras.setHours(0, 0, 0, 0);
      const fechaProducto = new Date(producto.createdAt);
      fechaProducto.setHours(0, 0, 0, 0);
      return matchesSearch && fechaProducto >= semanaAtras;
    }
    
    return matchesSearch;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-500">
        <p>Error al cargar el historial</p>
        <p className="text-sm text-gray-400">{(error as Error).message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            Historial de Productos Agregados
          </h2>
          <p className="text-sm text-gray-500">
            Total: {filteredProducts?.length || 0} productos
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setFilter('todos')}
            className={`px-3 py-1 text-sm rounded-full transition-all ${
              filter === 'todos'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Todos
          </button>
          <button
            onClick={() => setFilter('hoy')}
            className={`px-3 py-1 text-sm rounded-full transition-all ${
              filter === 'hoy'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Hoy
          </button>
          <button
            onClick={() => setFilter('semana')}
            className={`px-3 py-1 text-sm rounded-full transition-all ${
              filter === 'semana'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Última semana
          </button>

          {/* Botón de actualización manual */}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            title="Actualizar historial"
            className="p-1.5 text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-all ml-1 shadow-sm disabled:opacity-50"
          >
            <RefreshCw size={16} className={isFetching ? 'animate-spin text-blue-600' : ''} />
          </button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
        <Input
          type="text"
          placeholder="Buscar producto..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-600 text-sm">
                  <div className="flex items-center gap-1">
                    <Package size={14} />
                    Producto
                  </div>
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600 text-sm">
                  <div className="flex items-center gap-1">
                    <CalendarIcon size={14} />
                    Fecha
                  </div>
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600 text-sm">
                  <div className="flex items-center gap-1">
                    <Clock size={14} />
                    Hora
                  </div>
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600 text-sm">
                  Categoría
                </th>
                <th className="text-right py-3 px-4 font-semibold text-gray-600 text-sm">
                  Precio
                </th>
                <th className="text-right py-3 px-4 font-semibold text-gray-600 text-sm">
                  Stock
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts && filteredProducts.length > 0 ? (
                filteredProducts.map((producto) => (
                  <tr
                    key={producto.id}
                    className="border-b border-gray-100 hover:bg-blue-50/30 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <Package size={14} className="text-blue-600" />
                        </div>
                        <span className="font-medium text-gray-800">
                          {producto.nombre}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {formatDate(producto.createdAt)}
                    </td>
                    <td className="py-3 px-4 text-gray-500 font-mono text-sm">
                      {new Date(producto.createdAt).toLocaleTimeString('es-AR', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                        {producto.categoria || 'Sin categoría'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-gray-800">
                      {formatCurrency(Number(producto.precio))}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        producto.stock <= 2
                          ? 'bg-red-100 text-red-700'
                          : producto.stock <= 5
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {producto.stock} u.
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-400">
                    <Package size={48} className="mx-auto mb-4 text-gray-300" />
                    <p>No hay productos en el historial</p>
                    <p className="text-sm mt-2">Los productos agregados aparecerán aquí</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {filteredProducts && filteredProducts.length > 0 && (
        <div className="text-sm text-gray-500 text-center py-2 border-t border-gray-100">
          Mostrando {filteredProducts.length} de {data?.total || 0} productos
        </div>
      )}
    </div>
  );
}